import { MONTHS_PER_YEAR, round } from "./pricing";

export const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Bring a stored line up to the current shape. Estimates live in
 * localStorage, so lines saved by older builds (no id, no quantity, the
 * former "EC2" naming) have to keep working.
 */
export const migrateLine = (line) => {
  if (!line || typeof line !== "object") return null;

  const quantity = Number(line.quantity) > 0 ? Number(line.quantity) : 1;
  const monthlyCost = Number(line.monthlyCost) || 0;
  const unitMonthlyCost =
    line.unitMonthlyCost === undefined
      ? monthlyCost / quantity
      : Number(line.unitMonthlyCost) || 0;

  // "EC2 dd1.large" lines predate the rename of the section to Compute.
  const name =
    typeof line.name === "string" && line.name.startsWith("EC2 ")
      ? `Compute ${line.name.slice(4)}`
      : line.name || "Service";

  return {
    ...line,
    id: line.id || newId(),
    serviceId: line.serviceId || inferServiceId(name),
    name,
    description: line.description || "",
    quantity,
    unitMonthlyCost,
    monthlyCost: unitMonthlyCost * quantity,
    totalCost: unitMonthlyCost * quantity * MONTHS_PER_YEAR,
    items: Array.isArray(line.items) ? line.items : [],
    config: line.config ?? {},
    onRequest: Boolean(line.onRequest),
    dynamic: line.dynamic || null,
  };
};

const inferServiceId = (name) => {
  if (name.startsWith("Compute")) return "compute";
  if (name.startsWith("Network")) return "network";
  if (name.startsWith("S3") || name.startsWith("Storage")) return "storage";
  if (name.startsWith("GitLab")) return "software";
  if (name.startsWith("Database") || name.startsWith("Managed ")) {
    return "databases";
  }
  if (name.startsWith("Services")) return "managed-services";
  return "";
};

export const migrateLines = (lines) =>
  (Array.isArray(lines) ? lines : []).map(migrateLine).filter(Boolean);

/** Re-derive the money fields after quantity or unit price changed. */
export const withQuantity = (line, quantity) => {
  const qty = Number(quantity) > 0 ? Math.floor(Number(quantity)) : 1;
  return {
    ...line,
    quantity: qty,
    monthlyCost: line.unitMonthlyCost * qty,
    totalCost: line.unitMonthlyCost * qty * MONTHS_PER_YEAR,
  };
};

/**
 * Support plans are priced as "flat fee or a share of the infrastructure,
 * whichever is higher", so they can only be costed once every other line is
 * known. Those lines are stored with `dynamic: "support"` and resolved here.
 */
export const resolveDynamicLines = (lines) => {
  const list = Array.isArray(lines) ? lines : [];
  const infrastructure = list
    .filter((line) => !line.dynamic)
    .reduce((total, line) => total + (line.monthlyCost || 0), 0);

  return list.map((line) => {
    if (line.dynamic !== "support") return line;

    const flat = Number(line.meta?.flatPrice) || 0;
    const share = infrastructure * (Number(line.meta?.share) || 0);
    const monthlyCost = Math.max(flat, share);
    const percent = Math.round((Number(line.meta?.share) || 0) * 100);

    return {
      ...line,
      unitMonthlyCost: monthlyCost,
      monthlyCost,
      totalCost: monthlyCost * MONTHS_PER_YEAR,
      items: [
        {
          label: "Support plan",
          detail:
            share > flat
              ? `${percent}% of ${round(infrastructure)} € infrastructure`
              : `flat monthly minimum (above ${percent}% of ${round(
                  infrastructure
                )} €)`,
          quantity: 1,
          unit: "month",
          unitPrice: monthlyCost,
          cost: monthlyCost,
        },
      ],
    };
  });
};

export const totalMonthly = (lines) =>
  round(
    (lines || []).reduce((total, line) => total + (line.monthlyCost || 0), 0)
  );

// Derived from the per-line 12-month totals so it cannot drift from the
// monthly figure by a rounding step.
export const totalYearly = (lines) =>
  round((lines || []).reduce((total, line) => total + (line.totalCost || 0), 0));
